import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { OrganizationProvider } from "@/contexts/OrganizationContext";
import { ThemeProvider } from "@/hooks/use-theme";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { ManagerRoute } from "@/components/auth/ManagerRoute";
import { AdminRoute } from "@/components/auth/AdminRoute";
import { OrganizationLayout } from "@/components/layout/OrganizationLayout";
import { SubdomainRouter } from "@/components/SubdomainRouter";

const LandingPage = lazy(() => import("./pages/LandingPage"));
import { ScrollToTop } from "./components/ScrollToTop";

// Lazy-loaded routes
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const SelectOrganization = lazy(() => import("./pages/SelectOrganization"));
const SuperAdminDashboard = lazy(() => import("./pages/SuperAdminDashboard"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
// Org pages – lazy loaded (code-split away from landing page bundle)
const LearnerDashboard = lazy(() => import("./pages/org/LearnerDashboard"));
const MyCourses = lazy(() => import("./pages/org/MyCourses"));
const ManageUsers = lazy(() => import("./pages/org/ManageUsers"));
const ManageCourses = lazy(() => import("./pages/org/ManageCourses"));
const CourseEditor = lazy(() => import("./pages/org/CourseEditor"));
const OrgCourseViewer = lazy(() => import("./pages/org/OrgCourseViewer"));
const OrgSettings = lazy(() => import("./pages/org/OrgSettings"));
const CourseAnalytics = lazy(() => import("./pages/org/CourseAnalytics"));
const RolesPermissions = lazy(() => import("./pages/org/RolesPermissions"));
const Profile = lazy(() => import("./pages/org/Profile"));
const MyCertificatesPage = lazy(() => import("./pages/org/MyCertificatesPage"));
const UserDetail = lazy(() => import("./pages/org/UserDetail"));
const FAQ = lazy(() => import("./pages/org/FAQ"));
const CampaignsPage = lazy(() => import("./pages/org/CampaignsPage"));
const OnboardingPage = lazy(() => import("./pages/org/OnboardingPage"));
const LearningPathsPage = lazy(() => import("./pages/org/LearningPathsPage"));
const AuditLogPage = lazy(() => import("./pages/org/AuditLogPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const GuidePage = lazy(() => import("./pages/Guide"));
const Funksjoner = lazy(() => import("./pages/Funksjoner"));
const CaseStudy = lazy(() => import("./pages/CaseStudy"));
const Pricing = lazy(() => import("./pages/Pricing"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const Personvern = lazy(() => import("./pages/Personvern"));
const Kontakt = lazy(() => import("./pages/Kontakt"));
const Kundecase = lazy(() => import("./pages/Kundecase"));
const Innsikt = lazy(() => import("./pages/Innsikt"));
const OnboardingPublic = lazy(() => import("./pages/Onboarding"));
const VaareKurs = lazy(() => import("./pages/VaareKurs"));
const Servering = lazy(() => import("./pages/bransjer/Servering"));
const Transport = lazy(() => import("./pages/bransjer/Transport"));
const Barnehage = lazy(() => import("./pages/bransjer/Barnehage"));
const Retail = lazy(() => import("./pages/bransjer/Retail"));
const Finans = lazy(() => import("./pages/bransjer/Finans"));
const ByggAnlegg = lazy(() => import("./pages/bransjer/ByggAnlegg"));
const OmOss = lazy(() => import("./pages/OmOss"));
const TopicLanding = lazy(() => import("./pages/TopicLanding"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 60s — no refetch when user navigates back to a page within that window
      staleTime: 60_000,
      // Keep cached data for 5 min after components unmount — instant restore on tab switch
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    },
  },
});

// Expose page-prefetchers so the sidebar can warm them up on hover
export const pagePrefetchers: Record<string, () => Promise<unknown>> = {
  dashboard: () => import("./pages/org/LearnerDashboard"),
  courses: () => import("./pages/org/MyCourses"),
  certificates: () => import("./pages/org/MyCertificatesPage"),
  "manage/users": () => import("./pages/org/ManageUsers"),
  "manage/courses": () => import("./pages/org/ManageCourses"),
  analytics: () => import("./pages/org/CourseAnalytics"),
  onboarding: () => import("./pages/org/OnboardingPage"),
  settings: () => import("./pages/org/OrgSettings"),
  profile: () => import("./pages/org/Profile"),
  "learning-paths": () => import("./pages/org/LearningPathsPage"),
  campaigns: () => import("./pages/org/CampaignsPage"),
  "audit-log": () => import("./pages/org/AuditLogPage"),
  faq: () => import("./pages/org/FAQ"),
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <LanguageProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <AuthProvider>
            <OrganizationProvider>
              <SubdomainRouter>
                <Suspense fallback={<div className="min-h-screen bg-background" />}>
                <Routes>
                  {/* Public routes */}
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/guide" element={<GuidePage />} />
                  <Route path="/guide/:slug" element={<GuidePage />} />
                  <Route path="/funksjoner" element={<Funksjoner />} />
                  <Route path="/kundecase/cityservice" element={<CaseStudy />} />
                  <Route path="/kundecase/isdalen" element={<CaseStudy />} />
                  <Route path="/pris" element={<Pricing />} />
                  <Route path="/faq" element={<FAQPage />} />
                  <Route path="/personvern" element={<Personvern />} />
                  <Route path="/kontakt" element={<Kontakt />} />
                  <Route path="/kundecase" element={<Kundecase />} />
                  <Route path="/innsikt" element={<Innsikt />} />
                  <Route path="/onboarding" element={<OnboardingPublic />} />
                  <Route path="/kurs" element={<VaareKurs />} />
                  <Route path="/bransjer/servering" element={<Servering />} />
                  <Route path="/bransjer/transport" element={<Transport />} />
                  <Route path="/bransjer/barnehage" element={<Barnehage />} />
                  <Route path="/bransjer/retail" element={<Retail />} />
                  <Route path="/bransjer/finans" element={<Finans />} />
                  <Route path="/bransjer/bygg-anlegg" element={<ByggAnlegg />} />
                  <Route path="/om-oss" element={<OmOss />} />
                  <Route path="/lms" element={<TopicLanding topic="lms" />} />
                  <Route path="/learning-management-system" element={<TopicLanding topic="learning-management-system" />} />
                  <Route path="/e-laering" element={<TopicLanding topic="e-laering" />} />
                  <Route path="/laeringsplattform" element={<TopicLanding topic="laeringsplattform" />} />
                  <Route path="/opplaering-av-ansatte" element={<TopicLanding topic="opplaering-av-ansatte" />} />
                  <Route path="/kursplattform" element={<TopicLanding topic="kursplattform" />} />
                  
                  {/* Organization selection for logged in users */}
                  <Route
                    path="/organizations"
                    element={
                      <ProtectedRoute>
                        <SelectOrganization />
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* Super Admin */}
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute>
                        <SuperAdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* Organization Routes */}
                  <Route
                    path="/:orgSlug"
                    element={
                      <ProtectedRoute>
                        <OrganizationLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<LearnerDashboard />} />
                    <Route path="courses" element={<MyCourses />} />
                    <Route path="manage/users" element={<ManagerRoute><ManageUsers /></ManagerRoute>} />
                    <Route path="manage/courses" element={<AdminRoute><ManageCourses /></AdminRoute>} />
                    <Route path="manage/courses/:courseId" element={<AdminRoute><CourseEditor /></AdminRoute>} />
                    <Route path="course/:courseId" element={<OrgCourseViewer />} />
                    
                    <Route path="analytics" element={<ManagerRoute><CourseAnalytics /></ManagerRoute>} />
                    <Route path="settings" element={<OrgSettings />} />
                    <Route path="settings/organization" element={<AdminRoute><RolesPermissions /></AdminRoute>} />
                    <Route path="profile" element={<Profile />} />
                    <Route path="certificates" element={<MyCertificatesPage />} />
                    <Route path="user/:userId" element={<ManagerRoute><UserDetail /></ManagerRoute>} />
                    <Route path="manage/users/user/:userId" element={<ManagerRoute><UserDetail /></ManagerRoute>} />
                    <Route path="campaigns" element={<AdminRoute><CampaignsPage /></AdminRoute>} />
                    <Route path="onboarding" element={<ManagerRoute><OnboardingPage /></ManagerRoute>} />
                    <Route path="learning-paths" element={<LearningPathsPage />} />
                    <Route path="audit-log" element={<AdminRoute><AuditLogPage /></AdminRoute>} />
                    <Route path="faq" element={<FAQ />} />
                  </Route>
                  
                  <Route path="*" element={<NotFound />} />
                </Routes>
                </Suspense>
              </SubdomainRouter>
            </OrganizationProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
      </LanguageProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
